import { convexQuery, useConvexMutation } from '@convex-dev/react-query'
import { msg } from '@lingui/core/macro'
import { Trans, useLingui } from '@lingui/react/macro'
import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import type { ColumnDef, SortingState } from '@tanstack/react-table'
import { useTable } from '@tanstack/react-table'
import type { FunctionReturnType } from 'convex/server'
import { IconOffice2Outline18, IconTrashOutline18 } from 'nucleo-ui-outline-18'
import { useCallback, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { Button } from '~/components/ui/button'
import type { DataTableFeatures } from '~/components/ui/data-table'
import {
  DATE_FORMAT_OPTIONS,
  DataTable,
  dataTableFeatures,
  dataTableSortOptions,
  makeGlobalFilter,
} from '~/components/ui/data-table'
import { EmptyState } from '~/components/ui/empty-state'
import { PageHeader } from '~/components/ui/page-header'
import { SearchField } from '~/components/ui/search-field'
import { useDebouncedValue } from '~/hooks/use-debounced-value'
import { useGrowingPage } from '~/hooks/use-growing-page'
import { confirm } from '~/lib/confirm'
import { formatRelativeTime } from '~/lib/datetime'
import { titleFor } from '~/lib/route-title'
import { cn } from '~/lib/utils'
import { api } from '~convex/_generated/api'

export const Route = createFileRoute('/admin/workspaces/')({
  component: AdminWorkspaces,
  head: () => ({ meta: [{ title: titleFor(msg`Workspaces`) }] }),
})

const PAGE_SIZE = 200

type WorkspaceRow = FunctionReturnType<typeof api.admin.listWorkspaces>['page'][number]

/** Lifecycle chip: purging wins over deleted. */
const LifecycleBadge = ({ row }: { row: WorkspaceRow }) => {
  const badge = row.purgeStartedAt
    ? { className: 'bg-destructive/10 text-destructive', label: <Trans>Purging</Trans> }
    : row.deletedAt
      ? { className: 'bg-muted text-muted-foreground', label: <Trans>Deleted</Trans> }
      : null
  if (badge === null) {
    return null
  }
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 font-medium text-[11px]',
        badge.className
      )}
    >
      {badge.label}
    </span>
  )
}

const EMPTY_ROWS: WorkspaceRow[] = []

function AdminWorkspaces() {
  const { t, i18n } = useLingui()
  const navigate = useNavigate()
  const { paginationOpts, loadMore } = useGrowingPage(PAGE_SIZE)
  const { data, isPending } = useQuery(convexQuery(api.admin.listWorkspaces, { paginationOpts }))
  const { data: stats } = useQuery(convexQuery(api.admin.dashboardStats, {}))
  const [sorting, setSorting] = useState<SortingState>([{ desc: true, id: 'createdAt' }])
  const [globalFilter, setGlobalFilter] = useState('')
  // Server-side search catches workspaces beyond the loaded pages; results
  // merge into the table under the same (instant) client-side filter.
  const debouncedSearch = useDebouncedValue(globalFilter.trim())
  const { data: searchResults } = useQuery({
    ...convexQuery(api.admin.searchWorkspaces, { search: debouncedSearch }),
    enabled: debouncedSearch !== '',
  })
  const { mutateAsync: purge, isPending: isPurging } = useMutation({
    mutationFn: useConvexMutation(api.admin.purgeWorkspace),
  })

  const handlePurge = useCallback(
    async (row: WorkspaceRow) => {
      const confirmed = await confirm({
        confirmLabel: t`Purge`,
        description: t`Every trace of "${row.name}" will be erased, including all data, files, and audit history. This cannot be undone.`,
        destructive: true,
        icon: <IconTrashOutline18 />,
        title: t`Purge workspace?`,
      })
      if (!confirmed) {
        return
      }
      try {
        await purge({ workspaceId: row._id })
        toast.success(t`Purge started.`)
      } catch (error) {
        console.error('Purge workspace error:', error)
        toast.error(t`Couldn't start the purge.`)
      }
    },
    [t, purge]
  )

  const columns = useMemo<ColumnDef<DataTableFeatures, WorkspaceRow>[]>(
    () => [
      {
        accessorFn: (row) => row.name,
        cell: ({ row }) => (
          // Wrapping keeps the cell's min-content width down to the longest
          // word, so a long workspace name doesn't push the table wider than
          // a phone's viewport.
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span>{row.original.name}</span>
            <LifecycleBadge row={row.original} />
          </div>
        ),
        header: () => t`Workspace`,
        id: 'name',
        meta: { className: 'font-medium' },
      },
      {
        accessorFn: (row) => row.ownerName,
        cell: ({ row }) => (
          <div className="flex flex-col">
            <span>{row.original.ownerName}</span>
            {row.original.ownerEmail ? (
              <span className="break-all text-muted-foreground text-xs">
                {row.original.ownerEmail}
              </span>
            ) : null}
          </div>
        ),
        header: () => t`Owner`,
        id: 'owner',
        // The owner's email is the widest thing in the row; below `md` it
        // drops out and the detail page carries it instead.
        meta: { className: 'hidden md:table-cell' },
      },
      {
        accessorKey: 'memberCount',
        cell: ({ row }) => i18n.number(row.original.memberCount),
        header: () => t`Members`,
        meta: { className: 'hidden text-muted-foreground sm:table-cell' },
        sortDescFirst: true,
      },
      {
        accessorKey: 'projectCount',
        cell: ({ row }) => i18n.number(row.original.projectCount),
        header: () => t`Projects`,
        meta: { className: 'hidden text-muted-foreground sm:table-cell' },
        sortDescFirst: true,
      },
      {
        accessorKey: 'lastActiveAt',
        cell: ({ row }) =>
          row.original.lastActiveAt === null
            ? '—'
            : formatRelativeTime(row.original.lastActiveAt, i18n),
        header: () => t`Last active`,
        meta: { className: 'hidden text-muted-foreground lg:table-cell' },
        sortDescFirst: true,
      },
      {
        accessorKey: 'createdAt',
        cell: ({ row }) => i18n.date(row.original.createdAt, DATE_FORMAT_OPTIONS),
        header: () => t`Created`,
        meta: { className: 'hidden text-muted-foreground md:table-cell' },
        sortDescFirst: true,
      },
      {
        cell: ({ row }) => (
          <Button
            className="text-destructive hover:bg-destructive/10 hover:text-destructive"
            disabled={isPurging || row.original.purgeStartedAt !== null}
            onClick={(event) => {
              event.stopPropagation()
              handlePurge(row.original)
            }}
            size="sm"
            type="button"
            variant="ghost"
          >
            <IconTrashOutline18 />
            <Trans>Purge</Trans>
          </Button>
        ),
        enableSorting: false,
        header: () => null,
        id: 'actions',
        meta: { className: 'w-0 text-right' },
      },
    ],
    [t, i18n, isPurging, handlePurge]
  )

  const globalFilterFn = useMemo(
    () => makeGlobalFilter<WorkspaceRow>((row) => [row.name, row.ownerName, row.ownerEmail]),
    []
  )

  const loadedRows = data?.page ?? EMPTY_ROWS
  const rows = useMemo(() => {
    if (debouncedSearch === '' || searchResults === undefined) {
      return loadedRows
    }
    const seen = new Set(loadedRows.map((row) => row._id))
    return [...loadedRows, ...searchResults.filter((row) => !seen.has(row._id))]
  }, [loadedRows, debouncedSearch, searchResults])

  const table = useTable({
    ...dataTableSortOptions,
    columns,
    data: rows,
    features: dataTableFeatures,
    globalFilterFn,
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    state: { globalFilter, sorting },
  })

  const searching = globalFilter.trim() !== ''
  // While searching the count reflects matches; otherwise the real total
  // from `dashboardStats` (the loaded-page length undercounts past page 1).
  const headerCount = searching
    ? table.getRowModel().rows.length
    : (stats?.workspaceCount ?? loadedRows.length)

  return (
    <div className="flex h-full flex-col">
      <PageHeader count={headerCount} title={t`Workspaces`}>
        <SearchField onChange={setGlobalFilter} value={globalFilter} />
      </PageHeader>

      <div className="min-h-0 flex-1 overflow-y-auto px-3 pb-4">
        <DataTable
          empty={
            <EmptyState
              className="animation-duration-30 animate-fade-up"
              description={
                searching ? t`Try another search term.` : t`No workspaces exist on Planno yet.`
              }
              icon={IconOffice2Outline18}
              title={searching ? t`No workspaces found` : t`No workspaces yet`}
            />
          }
          isPending={isPending}
          onRowClick={(row) =>
            navigate({ params: { workspaceId: row._id }, to: '/admin/workspaces/$workspaceId' })
          }
          table={table}
        />
        {data !== undefined && !data.isDone ? (
          <div className="flex justify-center py-3">
            <Button onClick={loadMore} size="sm" type="button" variant="outline">
              <Trans>Load more</Trans>
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  )
}
